// File: Pages/Reports/components/forms/DynamicForm.jsx

import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { isValidUrlOrId, isValidYoutubeUrl, validateField } from '../../utils/reportValidation'
import PreviousPagePreview from '../PreviousPagePreview'
import { useAPI } from '../../../../Contexts/APIContext'
import FormComponents from './FormComponents'
import EnhancedSelect from '../EnhancedSelect'

/**
 * Dynamic Form Generator
 * Creates a form based on a JSON configuration
 */
const DynamicForm = ({ 
  formConfig, 
  onSubmit, 
  isSubmitting, 
  prevPageUrl, 
  prevPageTitle 
}) => {
  const { searchArtists, getAlbums } = useAPI()
  
  // Initialize form state based on config
  const initialFormState = formConfig.fields.reduce((state, field) => {
    switch(field.type) {
      case 'checkbox':
        state[field.name] = field.defaultValue || false
        break
      case 'multi-checkbox':
        state[field.name] = field.defaultValue || []
        break
      case 'select':
        state[field.name] = field.defaultValue || ''
        break
      default:
        state[field.name] = field.defaultValue || ''
    }
    return state
  }, {})

  // Initialize errors state
  const initialErrors = formConfig.fields.reduce((errors, field) => {
    errors[field.name] = null
    return errors
  }, {})

  const [formState, setFormState] = useState(initialFormState)
  const [errors, setErrors] = useState(initialErrors)
  const [dynamicData, setDynamicData] = useState({
    artistOptions: [],
    albumOptions: [],
    selectedArtistName: '',
    isLoadingArtists: false,
    isLoadingAlbums: false,
    selectedAlbums: [],
    artistSearchTerm: '',
    albumSearchTerm: ''
  })

  // Handle previous page selection
  const handlePrevPageSelect = (url) => {
    if (formConfig.usePreviousPage) {
      const prevPageFieldName = formConfig.previousPageField || 'feelsItemLink'
      setFormState(prev => ({
        ...prev,
        [prevPageFieldName]: url
      }))
      
      setErrors(prev => ({
        ...prev,
        [prevPageFieldName]: null
      }))
    }
  }

  // Search artists with debounce
  const handleArtistSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setDynamicData(prev => ({ 
        ...prev, 
        artistSearchTerm: term,
        artistOptions: []
      }))
      return
    }
    
    setDynamicData(prev => ({ 
      ...prev, 
      artistSearchTerm: term,
      isLoadingArtists: true 
    }))
    
    try {
      const artistField = formConfig.fields.find(field => 
        field.type === 'select' && field.sourceType === 'artists'
      )

      const searchAll = artistField && artistField.searchAll === true
      // console.log(searchAll)

      const response = await searchArtists(term, 1, 20, searchAll)
      // console.log(response)
      
      if (response && response.artists) {
        setDynamicData(prev => ({ ...prev, artistOptions: response.artists }))
      }
    } catch (error) {
      console.error('Error searching artists:', error)
    } finally {
      setDynamicData(prev => ({ ...prev, isLoadingArtists: false }))
    }
  }, [searchArtists])
  
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (dynamicData.artistSearchTerm) {
        handleArtistSearch(dynamicData.artistSearchTerm)
      }
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [dynamicData.artistSearchTerm, handleArtistSearch])

  // Fetch albums when artist is selected
  useEffect(() => {
    const albumField = formConfig.fields.find(field => 
      field.type === 'multi-checkbox' && field.sourceType === 'albums'
    )
    
    if (albumField && dynamicData.selectedArtistName) {
      const loadAlbums = async () => {
        const artistUsername = window.artistMap ? window.artistMap[dynamicData.selectedArtistName + '_username'] : null
        if (!artistUsername) return

        const artistField = formConfig.fields.find(field => 
          field.type === 'select' && field.sourceType === 'artists'
        )

        const searchAll = artistField && artistField.searchAll === true
        
        try {
          setDynamicData(prev => ({ ...prev, isLoadingAlbums: true }))
          const response = await getAlbums(artistUsername, 1, 100, searchAll)
          
          if (response && response.albums) {
            // Store album data for later use
            const albums = response.albums.map(album => ({
              value: album.f_id,
              label: album.name,
            }))
            setDynamicData(prev => ({ 
              ...prev, 
              albumOptions: albums,
            }))
            
            const albumMap = {}
            albums.forEach(album => {
              albumMap[album.label] = album.value
            })
            window.albumMap = albumMap
          }
        } catch (error) {
          console.error('Error loading albums:', error)
        } finally {
          setDynamicData(prev => ({ ...prev, isLoadingAlbums: false }))
        }
      }
      
      loadAlbums()
    }
  }, [dynamicData.selectedArtistName, getAlbums])

  // Handle album search filtering
  const getFilteredAlbums = useCallback(() => {
    if (!dynamicData.albumOptions.length) return []
    if (!dynamicData.albumSearchTerm) return dynamicData.albumOptions
    
    return dynamicData.albumOptions.filter(album => 
      album.label.toLowerCase().includes(dynamicData.albumSearchTerm.toLowerCase())
    )
  }, [dynamicData.albumOptions, dynamicData.albumSearchTerm])

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value
    
    setFormState(prev => ({
      ...prev,
      [name]: newValue
    }))
    
    setErrors(prev => ({
      ...prev,
      [name]: null
    }))
    
    // Handle conditional fields
    const field = formConfig.fields.find(field => field.name === name)
    if (field && field.affectsFields) {
      field.affectsFields.forEach(affectedField => {
        if (typeof affectedField === 'string') {
          // Simple reset of a field
          setFormState(prev => ({
            ...prev,
            [affectedField]: initialFormState[affectedField]
          }))
        } else if (typeof affectedField === 'object') {
          // Conditional logic
          const { fieldName, condition, resetWhen } = affectedField
          if ((condition === 'equals' && newValue === resetWhen) || 
              (condition === 'notEquals' && newValue !== resetWhen)) {
            setFormState(prev => ({
              ...prev,
              [fieldName]: initialFormState[fieldName]
            }))
          }
        }
      })
    }
  }
  
  // Handle artist selection
  // Updated handleArtistSelect function
  const handleArtistSelect = (selectedArtist) => {
    const artistName = typeof selectedArtist === 'string' ? selectedArtist : selectedArtist.name
    const artistId = typeof selectedArtist === 'string' ? '' : selectedArtist.f_id
    const artistUsername = typeof selectedArtist === 'string' ? selectedArtist : selectedArtist.artist_name;
    
    setDynamicData(prev => ({ 
      ...prev, 
      selectedArtistName: artistName, 
      artistSearchTerm: artistName,
      albumSearchTerm: '',
    }))
    
    const artistIdField = formConfig.fields.find(field => field.sourceIdFrom === 'artist')
    if (artistIdField) {
      setFormState(prev => ({
        ...prev,
        [artistIdField.name]: artistId,
        artistName: artistName,
        artistUsername: artistUsername
      }))
      
      setErrors(prev => ({
        ...prev,
        [artistIdField.name]: null,
        artistName: null,
        artistUsername: null
      }))
    }
    
    // Reset any album-related fields when artist changes
    const albumField = formConfig.fields.find(field => 
      field.type === 'multi-checkbox' && field.sourceType === 'albums'
    )
    if (albumField) {
      setFormState(prev => ({
        ...prev,
        [albumField.name]: []
      }))
      setDynamicData(prev => ({ ...prev, selectedAlbums: [] }))
    }
    
    // If we have a valid artist object, store for album loading
    if (typeof selectedArtist !== 'string' && selectedArtist.artist_name) {
      window.artistMap = {
        ...(window.artistMap || {}),
        [artistName]: selectedArtist.f_id,
        [artistName + '_username']: selectedArtist.artist_name
      }
    }
  }

  // Handle album search
  const handleAlbumSearch = (term) => {
    const searchTerm = typeof term === 'object' && term.target 
      ? term.target.value 
      : term;
      
    setDynamicData(prev => ({ ...prev, albumSearchTerm: searchTerm }))
  }
  
  // Handle option selection for custom select inputs
  const handleOptionSelect = (field, selectedValue) => {
    setFormState(prev => ({
      ...prev,
      [field.name]: selectedValue
    }))
    
    setErrors(prev => ({
      ...prev,
      [field.name]: null
    }))
    
    // If this is an artist field, also update the artist data
    if (field.sourceType === 'artists') {
      handleArtistSelect(selectedValue)
    }
    
    // Handle conditional fields
    if (field.affectsFields) {
      field.affectsFields.forEach(affectedField => {
        if (typeof affectedField === 'string') {
          setFormState(prev => ({
            ...prev,
            [affectedField]: initialFormState[affectedField]
          }))
        } else if (typeof affectedField === 'object') {
          const { fieldName, condition, resetWhen } = affectedField
          if ((condition === 'equals' && selectedValue === resetWhen) || 
              (condition === 'notEquals' && selectedValue !== resetWhen)) {
            setFormState(prev => ({
              ...prev,
              [fieldName]: initialFormState[fieldName]
            }))
          }
        }
      })
    }
  }
  
  // Update the handleMultiCheckboxToggle function
const handleMultiCheckboxToggle = (field, option) => {
  let updatedSelected
  const optionValue = typeof option === 'string' ? option : option.value
  const isSelected = formState[field.name].some(id => id === optionValue)
  
  if (isSelected) {
    updatedSelected = formState[field.name].filter(id => id !== optionValue)
  } else {
    updatedSelected = [...formState[field.name], optionValue]
  }
  
  setFormState(prev => ({
    ...prev,
    [field.name]: updatedSelected
  }))
  
  // Update the UI state for selected albums
  if (field.sourceType === 'albums') {
    let updatedSelectedAlbums
    const isAlbumSelected = dynamicData.selectedAlbums.some(a => a.value === optionValue)
    
    if (isAlbumSelected) {
      updatedSelectedAlbums = dynamicData.selectedAlbums.filter(a => a.value !== optionValue)
    } else {
      updatedSelectedAlbums = [...dynamicData.selectedAlbums, option]
    }
    
    setDynamicData(prev => ({
      ...prev,
      selectedAlbums: updatedSelectedAlbums
    }))
  }
  
  setErrors(prev => ({
    ...prev,
    [field.name]: null
  }))
}

  const validateForm = () => {
    const newErrors = {}
    
    // Validate each field based on configuration
    formConfig.fields.forEach(field => {
      // Skip validation for fields that should be hidden based on conditions
      if (field.visibleWhen) {
        const { dependsOn, condition, value } = field.visibleWhen
        const dependentValue = formState[dependsOn]
        
        if ((condition === 'equals' && dependentValue !== value) || 
            (condition === 'notEquals' && dependentValue === value)) {
          newErrors[field.name] = null
          return
        }
      }
      
      let validationFn = null
      if (field.validation === 'url') {
        validationFn = isValidUrlOrId
      } else if (field.validation === 'youtube') {
        validationFn = isValidYoutubeUrl
      } else if (typeof field.validation === 'function') {
        validationFn = field.validation
      }
      
      newErrors[field.name] = validateField(
        field.label, 
        formState[field.name], 
        field.required, 
        validationFn
      )
    })
    
    if (formConfig.formValidation) {
      const formLevelErrors = formConfig.formValidation(formState, newErrors)
      Object.assign(newErrors, formLevelErrors)
    }
    
    setErrors(newErrors)
    
    return Object.values(newErrors).every(error => error === null)
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (validateForm()) {
      const submissionData = { ...formState }
      
      if (dynamicData.selectedArtistName && formConfig.includeArtistName) {
        submissionData.artistName = dynamicData.selectedArtistName
      }
      
      onSubmit(submissionData)
    }
  }

  // Handle artist search input
  const handleArtistSearchInput = (searchTerm) => {
    const term = typeof searchTerm === 'object' && searchTerm.target 
      ? searchTerm.target.value 
      : searchTerm;
      
    setDynamicData(prev => ({ ...prev, artistSearchTerm: term }))
    
    // Only trigger search if term is at least 2 characters
    if (term && term.length >= 2) {
      //TODO: Search here
    } else if (!term) {
      // Clear artist options if search term is empty
      setDynamicData(prev => ({ ...prev, artistOptions: [] }))
    }
  }

  // Render a field based on its configuration
  const renderField = (field) => {
    if (field.visibleWhen) {
      const { dependsOn, condition, value } = field.visibleWhen
      const dependentValue = formState[dependsOn]
      
      if ((condition === 'equals' && dependentValue !== value) || 
          (condition === 'notEquals' && dependentValue === value)) {
        return null
      }
    }
    
    // Render the appropriate component based on field type
    switch (field.type) {
      case 'text':
        return (
          <FormComponents.TextField
            key={field.name}
            name={field.name}
            value={formState[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={isSubmitting}
            required={field.required}
            error={errors[field.name]}
            label={field.label}
            subtitle={field.subtitle}
          />
        )
      
      case 'textarea':
        return (
          <FormComponents.TextArea
            key={field.name}
            name={field.name}
            value={formState[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            disabled={isSubmitting}
            required={field.required}
            error={errors[field.name]}
            label={field.label}
            subtitle={field.subtitle}
          />
        )
      
      case 'select':
        let options = field.options || []
        let isLoading = false
        let placeholder = field.placeholder
        
        if (field.sourceType === 'artists') {
          options = dynamicData.artistOptions.map(artist => 
            typeof artist === 'string' ? artist : artist.name
          )
          isLoading = dynamicData.isLoadingArtists
          placeholder = isLoading ? "Loading artists..." : (field.placeholder || "Select an artist")
          
          return (
            <EnhancedSelect
              key={field.name}
              options={options}
              placeholder={placeholder}
              searchPlaceholder="Search artists..."
              defaultOption={dynamicData.selectedArtistName}
              onOptionSelect={(option) => {
                // Find the original artist object
                const artist = dynamicData.artistOptions.find(a => 
                  (typeof a === 'string' && a === option.value) || 
                  (typeof a === 'object' && a.name === option.value)
                )
                handleArtistSelect(artist || option.value)
              }}
              onSearch={handleArtistSearchInput}
              label={field.label}
              subtitle={field.subtitle}
              isRequired={field.required}
              disabled={isSubmitting}
              isLoading={isLoading}
              error={errors[field.name]}
              searchable={true}
              multiSelect={false}
            />
          )
        } else if (field.name === 'rightsCarryForward') {
          const yesNoOptions = ['yes', 'no'];
          
          return (
            <EnhancedSelect
              key={field.name}
              options={yesNoOptions.map(opt => ({ value: opt, label: opt }))}
              placeholder="Select option"
              searchPlaceholder="Filter options..."
              defaultOption={formState[field.name]}
              onOptionSelect={(option) => handleOptionSelect(field, option.value)}
              label={field.label}
              subtitle={field.subtitle}
              isRequired={field.required}
              disabled={isSubmitting}
              isLoading={false}
              error={errors[field.name]}
              searchable={false}
              multiSelect={false}
            />
          )

        }
        
        return (
          <EnhancedSelect
            key={field.name}
            options={options.map(opt => typeof opt === 'string' ? 
              { value: opt, label: opt } : 
              { value: opt.value || opt.name, label: opt.label || opt.name }
            )}
            placeholder={placeholder}
            defaultOption={formState[field.name]}
            onOptionSelect={(option) => handleOptionSelect(field, option.value)}
            label={field.label}
            subtitle={field.subtitle}
            isRequired={field.required}
            disabled={isSubmitting || isLoading}
            isLoading={isLoading}
            error={errors[field.name]}
            searchable={false}
            multiSelect={false}
          />
        )
      
      case 'checkbox':
        return (
          <FormComponents.CheckboxField
            key={field.name}
            name={field.name}
            checked={formState[field.name]}
            onChange={handleChange}
            disabled={isSubmitting}
            label={field.label}
            error={errors[field.name]}
          />
        )
      
      case 'date':
        return (
          <FormComponents.DateField
            key={field.name}
            name={field.name}
            value={formState[field.name]}
            onChange={handleChange}
            disabled={isSubmitting}
            required={field.required}
            error={errors[field.name]}
            label={field.label}
            subtitle={field.subtitle}
          />
        )
      
      case 'multi-checkbox':
        let multiOptions = field.options || []
        let selectedOptions = []
        let multiIsLoading = false
        
        if (field.sourceType === 'albums') {
          const filteredAlbums = getFilteredAlbums()
          multiOptions = filteredAlbums
          selectedOptions = dynamicData.selectedAlbums
          multiIsLoading = dynamicData.isLoadingAlbums
          
          return (
            <EnhancedSelect
              key={field.name}
              options={multiOptions}
              placeholder="Select albums"
              searchPlaceholder="Search albums..."
              selectedOptions={selectedOptions}
              onToggleOption={(option) => handleMultiCheckboxToggle(field, option)}
              onSearch={(term) => handleAlbumSearch(term)}
              label={field.label}
              subtitle={field.subtitle}
              isRequired={field.required}
              disabled={isSubmitting || !dynamicData.selectedArtistName}
              isLoading={multiIsLoading}
              error={errors[field.name]}
              searchable={true}
              multiSelect={true}
            />
          )

        } else {
          // Map IDs to options
          selectedOptions = formState[field.name].map(id => {
            const option = multiOptions.find(opt => opt.value === id)
            return option || { value: id, label: id }
          })
        }
        
        return (
          <FormComponents.MultiCheckboxField
            key={field.name}
            options={multiOptions}
            selectedOptions={selectedOptions}
            onToggle={(option) => handleMultiCheckboxToggle(field, option)}
            disabled={isSubmitting}
            required={field.required}
            error={errors[field.name]}
            label={field.label}
            subtitle={field.subtitle}
            isLoading={multiIsLoading}
            searchable={field.searchable !== false}
          />
        )
      
      default:
        return null
    }
  }
  return (
    <form onSubmit={handleSubmit}>
      {/* Previous page preview if configured to use it */}
      {formConfig.usePreviousPage && prevPageUrl && (
        <PreviousPagePreview
          pageUrl={prevPageUrl}
          pageTitle="Recently Visited Asset"
          onSelect={handlePrevPageSelect}
        />
      )}
      
      {/* Render all form fields */}
      {formConfig.fields.map(field => renderField(field))}
      
      {/* Submit button */}
      <FormComponents.SubmitButton 
        isSubmitting={isSubmitting}
        label={formConfig.submitButtonLabel}
      />
    </form>
  )
}

DynamicForm.propTypes = {
  formConfig: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string,
      subtitle: PropTypes.string,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      validation: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
      ]),
      defaultValue: PropTypes.any,
      options: PropTypes.array,
      sourceType: PropTypes.string,
      sourceIdFrom: PropTypes.string,
      visibleWhen: PropTypes.shape({
        dependsOn: PropTypes.string.isRequired,
        condition: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired
      }),
      affectsFields: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          fieldName: PropTypes.string.isRequired,
          condition: PropTypes.string.isRequired,
          resetWhen: PropTypes.any.isRequired
        })
      ])),
      searchable: PropTypes.bool
    })).isRequired,
    usePreviousPage: PropTypes.bool,
    previousPageField: PropTypes.string,
    includeArtistName: PropTypes.bool,
    submitButtonLabel: PropTypes.string,
    formValidation: PropTypes.func
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  prevPageUrl: PropTypes.string,
  prevPageTitle: PropTypes.string
}

export default DynamicForm